import { Button } from "@/components/ui/button";

import { StatusDot } from "./Marker.jsx";
import { ClosedMark } from "./ClosedMark.jsx";
import { NewMark } from "./NewMark.jsx";
import { Stars } from "./Stars.jsx";
import { WantMark } from "./WantMark.jsx";
import { ratingOf, reviewOf, statusOf } from "../lib/reviews.js";
import { cn, fmtDist } from "../lib/util.js";

/* 네이버·카카오 링크는 아이콘으로만 둔다.
   로고 원본을 그대로 박아 넣지는 않는다 — 기억으로 그린 패스는 미묘하게 틀어지고,
   틀어진 로고는 로고가 없는 것보다 나쁘다. 대신 각 사의 브랜드 컬러와 머리글자로
   세운 마크를 쓴다(초록 N / 노랑 k). 컬러만으로도 구분은 즉시 된다.
   글자가 사라지므로 이름은 aria-label 과 title 로 남긴다.

   hover 색을 브랜드 컬러로 한 번 더 못박는 이유: 아래 linkbtn 의 기본 hover 가
   액센트 파랑이라, 안 막으면 네이버 초록이 파랗게 변한다. */
const BRAND = {
  naver: {
    mark: "N", label: "네이버 지도에서 보기",
    tw: "bg-[#03c75a] text-white hover:bg-[#03c75a] hover:text-white",
  },
  kakao: {
    mark: "k", label: "카카오맵에서 보기",
    tw: "bg-[#fee500] text-[#191919] hover:bg-[#fee500] hover:text-[#191919]",
  },
};

/* 바깥 링크. Button 의 asChild 로 <a> 를 그대로 쓴다 —
   포커스 링·전환 처리를 공유하면서 마크업은 링크로 남는다. */
export function LinkBtn({ href, children, className, brand }) {
  const b = BRAND[brand];
  return (
    <Button
      asChild
      variant="outline"
      size="xs"
      className={cn(
        "linkbtn h-auto border-input px-[9px] py-[3px] text-[11.5px] font-normal text-muted-foreground shadow-none",
        "hover:border-primary hover:bg-transparent hover:text-primary",
        b && cn("brandbtn size-[22px] flex-none border-none p-0 text-[12.5px] font-extrabold",
                "leading-none hover:brightness-[.93]", b.tw),
        className,
      )}
    >
      <a href={href || "#"} target="_blank" rel="noopener" aria-label={b?.label} title={b?.label}>
        {b ? b.mark : children}
      </a>
    </Button>
  );
}

/* 말풍선 껍데기 — 한 곳을 읽는 말풍선(Bubble)과 겹친 곳들을 펴는 말풍선(ClusterBubble)이
   같은 상자를 쓴다. 여백·모서리·그림자가 갈라지면 지도 위에 다른 물건 둘이 뜬 것으로 보인다.

   폭만 부르는 쪽이 정한다. 한 곳짜리는 268px, 목록은 300px 이다 — 목록은 한 줄에
   이름·찜·거리를 얹고 그 아래 카테고리·별점·건수까지 얹으므로, 같은 폭에서 먼저
   터진다('전주콩부리콩나물국밥 서초점(대우디오빌프라임)'이 세 줄로 접혔다). 한 곳짜리는
   이름이 제 줄을 다 쓰므로 268px 로 충분하고, 넓히면 지도만 더 가린다.
   그 이상은 안 넓힌다: 360px 짜리 화면에서 말풍선이 지도를 통째로 덮기 시작한다.

   마커(원 24px + 꼬리 8px) 높이만큼 띄워 꼬리가 마커 머리에 닿게 한다.
   yAnchor 를 비율로 주면 내용 길이에 따라 간격이 달라진다.

   whitespace-normal 은 카카오를 되돌리는 것이다 — CustomOverlay 는 감싼 div 에
   white-space:nowrap 을 인라인으로 박는다. 상속되면 폭 지정은 아무 의미가 없어서
   긴 상호명이 한 줄로 흘러 말풍선 밖으로 삐져나가고 닫기 ✕ 를 덮는다.
   줄바꿈은 어절 단위로 한다(break-keep) — 기본값은 '서초점(대우디오빌 / 프라임빌딩)'
   처럼 낱말 한가운데를 자른다. 대신 띄어쓰기 없는 긴 이름은 어절이 곧 한 줄보다
   길어 다시 넘치므로, 그때만 아무 데서나 끊게 wrap-anywhere 를 안전망으로 둔다. */
const SHELL = "relative mb-9 flex cursor-default flex-col gap-[9px] rounded-xl "
  + "border border-border bg-popover p-[13px_14px_12px] text-left text-[13px] "
  + "leading-normal break-keep whitespace-normal wrap-anywhere "
  + "text-popover-foreground shadow-bubble";

/* 지도 위 말풍선 — 가기 전에 알아야 할 것을 지도 위에서 읽는다.
   기록은 시트에서 한다. 예전에는 여기서 판정 3버튼으로 탭 1회에 끝냈는데,
   목록 카드와 같은 자리에 별점·리뷰 수가 들어오면서 '읽는 곳'과 '쓰는 곳'을 갈랐다.
 *
 * **좁은 화면에서는 이름과 지도 링크만 남긴다**(아래 DETAIL). 폰의 지도는 46dvh 뿐이라
 * 다 편 말풍선이 그 절반을 덮는다 — 가리려고 만든 물건은 아니다. 남기는 둘은 이 화면에서만
 * 할 수 있는 일이라서 남는다: 어느 집인지 확인하는 것(이름), 그리고 길찾기로 건너가는 것
 * (네이버·카카오). 나머지는 지도 위에서 굳이 읽을 것이 아니고, 같은 값이 아래 목록 카드와
 * 상세 시트에 그대로 있다.
 *
 * 안 그리는 게 아니라 CSS 로 접는다. 가로세로를 뒤집거나 창을 넓히면 그 자리에서 다시
 * 펴져야 하는데, 자바스크립트로 가르면 그 경계마다 말풍선이 다시 만들어진다. */
/* 좁은 화면에서 접는 줄들. `hidden wide:flex` 가 아니라 `max-wide:hidden` 인 이유는
   줄마다 display 가 다르기 때문이다(sub 은 block, facts·rating 은 flex) — 넓은 화면 쪽을
   여기서 정해버리면 셋 중 둘의 배치가 바뀐다. 이 클래스는 감추기만 하고 원래 display 는
   각 줄이 계속 들고 있는다.
   자리도 같이 사라져서 SHELL 의 gap 이 남지 않는다 — 빈 틈이 '뭔가 안 그려졌다'로 읽히는
   일이 없다. */
const DETAIL = "max-wide:hidden";

export function Bubble({ store, rating, want, onToggleWant, onClose, onMore }) {
  const sub = [store.category || "기타"];
  if (store._dist != null) sub.push(`${Math.round(store._dist)}m`);
  if (store.walk_min) sub.push(`도보 ${store.walk_min}분`);

  /* 별점은 안 띄운다. 우리 기록으로만 매기기로 했다 — 남의 별점이 옆에 있으면
     아직 아무도 안 가본 집이 이미 평가된 것처럼 보인다. StoreCard 와 같은 기준이다. */
  const facts = [];
  if (store.hours_raw) {
    facts.push(<div className="bb-row flex items-baseline gap-1.5 text-[12.5px]" key="hours">{store.hours_raw}</div>);
  }
  if (store.address) {
    facts.push(
      <div className="bb-row bb-dim flex items-baseline gap-1.5 text-xs text-muted-foreground" key="addr">
        {store.address}
      </div>
    );
  }

  return (
    <div className={cn(SHELL, "bubble w-[268px]")} onClick={(e) => e.stopPropagation()}>
      <div className="bb-head flex items-start gap-2">
        {/* 방문 횟수는 여기 두지 않는다 — 아래 별점 줄의 '리뷰 N건'이 같은 수다.
            말풍선 한 폭 안에서 같은 숫자를 두 번 보여줄 자리가 없다. */}
        {/* 하트는 flex 칸이 아니라 이름 끝에 인라인으로 붙는다 — 칸으로 떼면 이름이
            두 줄이 되는 순간 하트만 세 번째 줄로 떨어져, 무엇에 붙은 하트인지 모를
            자리에 혼자 선다(목록·겹친 핀 목록도 같은 규칙). */}
        <div className="bb-title min-w-0 flex-1">
          <strong className="text-[15px] font-bold tracking-[-.01em]">{store.name}</strong>
          {/* '신규' 배지는 여기 두지 않는다. 이 말풍선은 한 곳을 이미 고른 뒤에
              들여다보는 화면이라, 훑다가 걸리게 하는 배지의 일이 끝난 자리다.
              폭도 268px 뿐이라 이름이 길면 배지가 하트를 아랫줄로 밀어낸다.
              찾는 일은 목록 카드와 겹친 핀 목록이 맡는다(NewMark.jsx).
              '임시 휴무'는 반대로 여기가 제일 필요한 자리다(ClosedMark.jsx). */}
          <ClosedMark store={store} className="ml-[7px]" />
          {/* 꺼져 있어도 빈 하트를 띄운다 — 한 곳을 보고 있는 화면이라 여기가
              표시하기 가장 편한 자리다. 목록에서는 반대로 켜진 것만 그린다. */}
          <WantMark on={want} onToggle={onToggleWant} showEmpty className="ml-[7px] align-middle" />
        </div>
        <Button
          variant="ghost"
          size="icon-xs"
          className="bb-close size-auto flex-none p-0 pt-0.5 pl-1 text-sm text-muted-foreground
                     hover:bg-transparent hover:text-foreground"
          aria-label="닫기"
          onClick={onClose}
        >
          ✕
        </Button>
      </div>

      <div className={cn("bb-sub -mt-1.5 text-xs text-muted-foreground", DETAIL)}>
        {sub.join(" · ")}
      </div>

      {facts.length > 0 && (
        <div className={cn("bb-facts flex flex-col gap-[3px] border-t border-border pt-[9px]", DETAIL)}>
          {facts}
        </div>
      )}

      <div className="bb-links flex flex-wrap items-center gap-[5px]">
        <LinkBtn href={store.links?.naver} brand="naver" />
        <LinkBtn href={store.links?.kakao} brand="kakao" />
      </div>

      {/* 별점 · 리뷰 수 — 목록 카드와 같은 모양이고, 시트로 가는 문도 같은 자리다.
          예전에는 여기 오른쪽 끝에 '리뷰하기'가 따로 있었다. 같은 곳으로 가는 길이
          화면마다 다른 이름·다른 자리에 있으면, 카드에서 익힌 조작이 지도에서는
          한 번 더 찾아야 하는 것이 된다. 왼쪽 목록과 같게 '리뷰 N건'을 누른다. */}
      <div className={cn("bb-rating flex items-baseline gap-1.5 border-t border-border pt-2.5 text-[12.5px]",
                         DETAIL)}>
        <b className="font-bold text-foreground">{rating.score.toFixed(1)}</b>
        <Stars score={rating.score} className="text-[11px]" />
        {/* 말풍선은 지도 클릭을 막아둔 상자 안이라 이벤트가 새어 나갈 일은 없지만,
            조작점이므로 키보드로도 닿아야 한다(StoreCard 의 같은 자리와 같은 규칙). */}
        <span
          role="button"
          tabIndex={0}
          className="bb-more reviews-open cursor-pointer whitespace-nowrap text-muted-foreground
                     underline-offset-2 hover:text-primary hover:underline"
          onClick={onMore}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onMore();
          }}
        >
          리뷰 {rating.count}건
        </span>
      </div>

      <div className="bb-tail" />
    </div>
  );
}

/* 겹친 핀을 누르면 뜨는 목록 — 한 자리에 포개져 있던 곳들을 편다.
   지도를 확대하게 만들지 않는 이유: 확대는 답이 아니라 숙제다. 한 건물에 넷이 있으면
   최대 배율에서도 안 갈라지고, 갈라진다 해도 그때는 옆 골목이 화면 밖으로 나가 있다.
   겹침은 화면의 문제이므로 화면에서 끝낸다.

   한 줄이 곧 한 가게다. 누르면 그 집 말풍선으로 바뀐다 — 여기서 별점·주소·링크까지
   다 펴면 말풍선 안에 든 목록이 아니라 그냥 작은 리스트가 되고, 지도는 통째로 가려진다.
   대신 훑어서 고를 만큼은 준다: 상태 점(마커에서 잃은 색) · 이름 · 거리 · 별점. */
export function ClusterBubble({ stores, reviews, onPick, onToggleWant, onClose }) {
  /* 리뷰 많은 순 · 같으면 이름 순. 들어온 순서는 거리순인데, 한 자리에 포개진 곳들이라
     거리 차가 몇 미터뿐이다 — 사실상 무작위 순서로 읽히고, 지도를 조금 밀어 묶음이
     다시 짜이면 줄 순서까지 바뀐다. 가본 집을 위로 올리는 편이 고르는 데 쓸모 있고,
     핀이 대표로 삼는 집(App.jsx headliner)과도 같은 기준이다. 이름순 되물림은
     0건끼리의 순서를 배율과 무관하게 못박기 위한 것이다. */
  const rows = [...stores].sort(
    (a, b) => ratingOf(reviews, b.id).count - ratingOf(reviews, a.id).count
      || a.name.localeCompare(b.name, "ko"),
  );

  return (
    <div className={cn(SHELL, "cluster w-[300px]")} id="cluster" onClick={(e) => e.stopPropagation()}>
      <div className="bb-head flex items-start gap-2">
        <strong className="flex-1 text-[15px] font-bold tracking-[-.01em]">
          이 자리에 {stores.length}곳
        </strong>
        <Button
          variant="ghost"
          size="icon-xs"
          className="bb-close size-auto flex-none p-0 pt-0.5 pl-1 text-sm text-muted-foreground
                     hover:bg-transparent hover:text-foreground"
          aria-label="닫기"
          onClick={onClose}
        >
          ✕
        </Button>
      </div>

      {/* 한 건물에 열 곳 넘게 든 자리가 실제로 있다. 말풍선이 지도를 다 먹지 않게
          여기서 자르고 굴린다 — 잘린 줄이 있다는 건 스크롤 막대가 말해준다.
          216px(네 줄 남짓)이던 것을 320px 로 늘렸다: 여덟 곳이 든 자리에서 세 줄만
          보이면 나머지는 굴려야 있는 줄도 모른다. 상한을 vh 로 두지 않는 이유는
          말풍선이 핀 위에 서기 때문이다 — 화면 높이를 다 쓰면 지도 밖으로 자란다.
          -mx-1.5 는 줄 hover 판이 말풍선 안쪽 여백까지 덮게 하려는 것이다. */}
      <div className="cl-list -mx-1.5 flex max-h-[320px] flex-col overflow-y-auto
                      border-t border-border pt-1.5">
        {rows.map((s) => {
          const r = reviewOf(reviews, s.id);
          const { score, count } = ratingOf(reviews, s.id);
          return (
            <button
              key={s.id}
              type="button"
              className="cl-row flex w-full items-start gap-2 rounded-lg px-1.5 py-[7px]
                         text-left hover:bg-muted"
              data-id={s.id}
              onClick={() => onPick(s.id)}
            >
              <StatusDot status={statusOf(reviews, s)} />
              <div className="min-w-0 flex-1">
                {/* 이름·하트·거리를 한 줄에 늘어놓고 접힘을 flex-wrap 에 맡기지 않는다 —
                    이름이 두 줄이 되는 순간 ml-auto 로 오른쪽에 밀어둔 거리가 제 줄로
                    떨어져, 빈 줄 끝에 '59m' 혼자 남는다(StoreList.jsx 도 같은 규칙).
                    거리는 폭이 뻔한 칸이라 접히는 이름 옆에 세워두면 그만이고, 이름
                    상자는 남는 폭 안에서 접힌다. 하트는 이름 끝에 붙어야 뜻이 사는 표식이라
                    별도 칸으로 떼지 않고 이름과 같은 글줄에 인라인으로 흘린다. */}
                <div className="cl-name flex items-baseline gap-[6px]">
                  <div className="min-w-0 flex-1">
                    <strong className="text-[13.5px] font-bold tracking-[-.01em]">{s.name}</strong>
                    {/* 이름이 13.5px 라 배지도 한 단계 줄인다. 한 자리에 열 곳 넘게
                        든 목록이라 기본 크기로는 줄마다 배지가 이름만큼 눈에 띈다. */}
                    <NewMark store={s} className="ml-[6px] px-1 text-[9.5px]" />
                    <ClosedMark store={s} className="ml-[6px] px-1 text-[9.5px]" />
                    {/* 목록과 같은 규칙 — 켜진 것만 그린다(WantMark.jsx). */}
                    <WantMark on={r.want_to_go} onToggle={() => onToggleWant(s.id)}
                              className="ml-[6px] align-middle" />
                  </div>
                  {s._dist != null && (
                    <span className="flex-none text-[11.5px] text-muted-foreground">
                      {fmtDist(s._dist)}
                    </span>
                  )}
                </div>
                {/* 별점 줄은 접히면 안 된다 — '카페/베이커리/패스트푸드' 같은 긴
                    카테고리가 밀어내면 '3건'만 다음 줄로 떨어져 미아가 된다.
                    자리를 양보하는 쪽은 언제나 카테고리다(min-w-0 + truncate). */}
                <div className="cl-meta flex items-baseline gap-1.5 text-xs text-muted-foreground">
                  <span className="min-w-0 truncate">{s.category || "기타"}</span>
                  <span className="ml-auto flex flex-none items-baseline gap-1.5">
                    <b className="font-bold text-foreground">{score.toFixed(1)}</b>
                    <Stars score={score} className="text-[10.5px]" />
                    {count}건
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bb-tail" />
    </div>
  );
}
